import React, { useEffect, useMemo, useState } from "react";
import { api, downloadPdf } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  UtensilsCrossed, Plus, Trash2, Download, Loader2, Send, Search,
  CalendarDays, DollarSign, ClipboardList, Save, Receipt, Wallet, BookOpenCheck, CheckCircle2, Circle,
} from "lucide-react";

const CONCEPTS = [
  { key: "desayuno", label: "Desayuno" },
  { key: "comida", label: "Comida" },
  { key: "completo", label: "Completo" },
];
const MODALITIES = [
  { key: "dia", label: "Por día" },
  { key: "mensual", label: "Mensual" },
];
const conceptLabel = (k) => (CONCEPTS.find((c) => c.key === k) || {}).label || k;
const modalityLabel = (k) => (MODALITIES.find((m) => m.key === k) || {}).label || k;
const money = (v) => "$" + Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export default function Comedor() {
  const [view, setView] = useState("diario");
  const [prices, setPrices] = useState(null);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    api.get("/comedor/config").then((r) => setPrices(r.data.prices)).catch(() => setPrices(null));
    api.get("/students", { params: { kind: "student" } }).then((r) => setStudents(r.data || [])).catch(() => {});
  }, []);

  const TABS = [
    { key: "diario", label: "Registro diario", icon: ClipboardList },
    { key: "adeudos", label: "Adeudos", icon: Wallet },
    { key: "menu", label: "Menú semanal", icon: BookOpenCheck },
    { key: "reportes", label: "Reportes", icon: CalendarDays },
    { key: "precios", label: "Precios", icon: DollarSign },
  ];

  return (
    <div data-testid="comedor-panel">
      <div className="mb-4">
        <h2 className="font-display font-bold text-lg text-navy flex items-center gap-2"><UtensilsCrossed className="w-5 h-5 text-navy-light" /> Comedor escolar</h2>
        <p className="text-xs text-nardo-500">Registra a diario quién compra comedor, genera comprobantes para los papás y descarga reportes por día, semana, mes o año.</p>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setView(key)} data-testid={`comedor-subtab-${key}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${view === key ? "bg-navy text-white" : "bg-white border border-nardo-200 text-nardo-600 hover:text-navy"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {view === "diario" && <RegistroDiario students={students} prices={prices} />}
      {view === "adeudos" && <Adeudos students={students} prices={prices} />}
      {view === "menu" && <MenuSemanal />}
      {view === "reportes" && <Reportes />}
      {view === "precios" && <Precios prices={prices} onSaved={setPrices} />}
    </div>
  );
}

function StudentPicker({ students, value, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (students || []).filter((x) => !s || (x.full_name || "").toLowerCase().includes(s)).slice(0, 40);
  }, [students, q]);
  const selected = students.find((x) => x.id === value);
  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-4 h-4 text-nardo-400 absolute left-2.5 top-2.5" />
        <input
          value={open ? q : (selected ? selected.full_name : q)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQ(""); }}
          placeholder="Buscar alumno…" data-testid="comedor-student-search"
          className="w-full border border-nardo-200 rounded-lg pl-8 pr-3 py-2 text-sm text-navy outline-none focus:border-navy" />
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-[10]" onClick={() => setOpen(false)} />
          <div className="absolute z-[20] mt-1 w-full max-h-60 overflow-auto bg-white border border-nardo-200 rounded-lg shadow-xl" data-testid="comedor-student-list">
            {filtered.length === 0 ? <div className="px-3 py-2 text-sm text-nardo-400">Sin resultados</div> :
              filtered.map((x) => (
                <button key={x.id} onClick={() => { onPick(x); setOpen(false); setQ(""); }} data-testid={`comedor-student-opt-${x.id}`}
                  className="w-full text-left px-3 py-2 text-sm text-navy hover:bg-nardo-50 flex items-center justify-between gap-2">
                  <span className="truncate">{x.full_name}</span>
                  <span className="text-[11px] text-nardo-400 shrink-0">{x.group_label || x.group || ""}</span>
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

function RegistroDiario({ students, prices }) {
  const { user } = useAuth();
  const canDelete = user?.role !== "staff"; // Solo Dirección (owner/member) puede eliminar registros; el administrador no.
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [student, setStudent] = useState(null);
  const [concept, setConcept] = useState("comida");
  const [modality, setModality] = useState("dia");
  const [amount, setAmount] = useState("");
  const [adding, setAdding] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const load = () => {
    setData(null);
    api.get("/comedor/orders", { params: { date } }).then((r) => setData(r.data)).catch(() => setData({ orders: [], totals: { count: 0, amount: 0, by_concept: {} } }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  // Autocompleta el monto desde los precios configurados al cambiar concepto/modalidad.
  useEffect(() => {
    if (prices && prices[concept]) setAmount(String(prices[concept][modality] || ""));
  }, [concept, modality, prices]);

  const add = async () => {
    if (!student) { toast.error("Elige el alumno"); return; }
    setAdding(true);
    try {
      await api.post("/comedor/orders", {
        student_id: student.id, student_name: student.full_name, group: student.group || "",
        date, concept, modality, amount: Number(amount || 0),
      });
      toast.success("Registrado en el comedor");
      setStudent(null); setAmount(prices && prices[concept] ? String(prices[concept][modality] || "") : "");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo registrar"); }
    finally { setAdding(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Quitar este registro?")) return;
    try { await api.delete(`/comedor/orders/${id}`); load(); } catch { toast.error("No se pudo eliminar"); }
  };
  const notify = async () => {
    setNotifying(true);
    try {
      const { data: r } = await api.post("/comedor/notify", { date });
      toast.success(`Registro enviado a Dirección y Coordinación (${r.count} alumno[s])`);
    } catch { toast.error("No se pudo enviar"); }
    finally { setNotifying(false); }
  };

  const t = data?.totals || { count: 0, amount: 0 };
  const inp = "border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy";

  return (
    <div className="space-y-4" data-testid="comedor-diario">
      {/* Fecha + acciones */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <label className="inline-flex items-center gap-2 text-sm text-navy font-medium">
          <CalendarDays className="w-4 h-4 text-navy-light" /> Fecha:
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="comedor-date" className={inp} />
        </label>
        <div className="flex gap-2">
          <button onClick={notify} disabled={notifying || !data?.orders?.length} data-testid="comedor-send" className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors">{notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar a Dirección y Coordinación</button>
          <button onClick={() => downloadPdf("/comedor/report.pdf", { params: { period: "day", anchor: date }, filename: `comedor_${date}.pdf` })} disabled={!data?.orders?.length} data-testid="comedor-day-pdf" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"><Download className="w-4 h-4" /> Lista del día (PDF)</button>
        </div>
      </div>

      {/* Agregar */}
      <div className="bg-white border border-nardo-200 rounded-xl p-4">
        <div className="grid gap-3 md:grid-cols-[1.6fr_1fr_1fr_0.9fr_auto] items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Alumno</label>
            <StudentPicker students={students} value={student?.id} onPick={setStudent} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Concepto</label>
            <select value={concept} onChange={(e) => setConcept(e.target.value)} data-testid="comedor-concept" className={`${inp} w-full`}>
              {CONCEPTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Modalidad</label>
            <select value={modality} onChange={(e) => setModality(e.target.value)} data-testid="comedor-modality" className={`${inp} w-full`}>
              {MODALITIES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Costo pagado</label>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="comedor-amount" placeholder="0.00" className={`${inp} w-full`} />
          </div>
          <button onClick={add} disabled={adding} data-testid="comedor-add" className="inline-flex items-center justify-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 h-[38px]">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Agregar</button>
        </div>
        {student && <p className="text-[11px] text-nardo-500 mt-2">Seleccionado: <span className="font-semibold text-navy">{student.full_name}</span> {student.group_label ? `· ${student.group_label}` : ""}</p>}
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Alumnos del día</div><div className="text-2xl font-display font-extrabold text-navy">{t.count}</div></div>
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Total cobrado</div><div className="text-2xl font-display font-extrabold text-emerald-600">{money(t.amount)}</div></div>
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center col-span-2 sm:col-span-1"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Por concepto</div><div className="text-[12px] text-navy font-semibold mt-1">D {t.by_concept?.desayuno || 0} · C {t.by_concept?.comida || 0} · Comp {t.by_concept?.completo || 0}</div></div>
      </div>

      {/* Lista */}
      <div className="bg-white border border-nardo-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-nardo-100 font-display font-bold text-navy text-sm">Lista del {date}</div>
        {data === null ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div> :
          data.orders.length === 0 ? <div className="py-12 text-center text-nardo-500 text-sm" data-testid="comedor-empty">Nadie registrado en el comedor este día.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-navy/5 text-navy text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-2">Alumno</th><th className="text-left px-2 py-2">Grupo</th><th className="text-left px-2 py-2">Concepto</th><th className="text-left px-2 py-2">Modalidad</th><th className="text-right px-2 py-2">Costo</th><th className="px-2 py-2 w-28"></th>
              </tr></thead>
              <tbody className="divide-y divide-nardo-100">
                {data.orders.map((o) => (
                  <tr key={o.id} data-testid={`comedor-row-${o.id}`}>
                    <td className="px-4 py-2 font-medium text-navy">{o.student_name}</td>
                    <td className="px-2 py-2 text-nardo-600">{o.group_label || "—"}</td>
                    <td className="px-2 py-2 text-nardo-600">{conceptLabel(o.concept)}</td>
                    <td className="px-2 py-2 text-nardo-600">{modalityLabel(o.modality)}</td>
                    <td className="px-2 py-2 text-right font-semibold text-emerald-700">{money(o.amount)}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => downloadPdf(`/comedor/orders/${o.id}/receipt.pdf`, { filename: `comprobante_${o.student_name}.pdf` })} data-testid={`comedor-receipt-${o.id}`} title="Comprobante para el papá" className="inline-flex items-center justify-center border border-emerald-500/40 text-emerald-700 hover:bg-emerald-600 hover:text-white px-2 py-1 rounded-lg transition-colors"><Receipt className="w-3.5 h-3.5" /></button>
                        {canDelete && <button onClick={() => remove(o.id)} data-testid={`comedor-del-${o.id}`} title="Quitar" className="inline-flex items-center justify-center border border-red-300 text-red-600 hover:bg-red-500 hover:text-white px-2 py-1 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>
    </div>
  );
}

function Reportes() {
  const [period, setPeriod] = useState("day");
  const [anchor, setAnchor] = useState(todayStr());
  const [group, setGroup] = useState("");
  const [grades, setGrades] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/direccion/segments").then((r) => setGrades(r.data.grades || [])).catch(() => {}); }, []);
  const load = () => {
    setData(null);
    const p = { period, anchor }; if (group) p.group = group;
    api.get("/comedor/orders", { params: p }).then((r) => setData(r.data)).catch(() => setData({ orders: [], totals: { count: 0, amount: 0, by_concept: {} }, period_label: "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, anchor, group]);

  const PERIODS = [{ k: "day", l: "Día" }, { k: "week", l: "Semana" }, { k: "month", l: "Mes" }, { k: "year", l: "Año" }];
  const t = data?.totals || { count: 0, amount: 0, by_concept: {} };
  const inp = "border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy";

  return (
    <div className="space-y-4" data-testid="comedor-reportes">
      <div className="bg-white border border-nardo-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-nardo-500 mb-1">Periodo</div>
          <div className="flex gap-1.5">
            {PERIODS.map((p) => (
              <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`comedor-period-${p.k}`} className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${period === p.k ? "bg-navy text-white" : "bg-nardo-50 border border-nardo-200 text-nardo-600 hover:text-navy"}`}>{p.l}</button>
            ))}
          </div>
        </div>
        <label className="text-sm text-navy">
          <div className="text-[10px] uppercase tracking-wider text-nardo-500 mb-1">Fecha de referencia</div>
          <input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)} data-testid="comedor-anchor" className={inp} />
        </label>
        <label className="text-sm text-navy">
          <div className="text-[10px] uppercase tracking-wider text-nardo-500 mb-1">Grupo</div>
          <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="comedor-report-group" className={inp}>
            <option value="">Todos los grupos</option>
            {grades.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </label>
        <button onClick={() => downloadPdf("/comedor/report.pdf", { params: { period, anchor, ...(group ? { group } : {}) }, filename: `comedor_${period}.pdf` })} data-testid="comedor-report-pdf" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold ml-auto"><Download className="w-4 h-4" /> Descargar PDF</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center col-span-2 sm:col-span-1"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Periodo</div><div className="text-sm font-semibold text-navy mt-1">{data?.period_label || "—"}</div></div>
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Registros</div><div className="text-2xl font-display font-extrabold text-navy">{t.count}</div></div>
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Total</div><div className="text-2xl font-display font-extrabold text-emerald-600">{money(t.amount)}</div></div>
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Por concepto</div><div className="text-[12px] text-navy font-semibold mt-1">D {t.by_concept?.desayuno || 0} · C {t.by_concept?.comida || 0} · Comp {t.by_concept?.completo || 0}</div></div>
      </div>

      <div className="bg-white border border-nardo-200 rounded-xl overflow-hidden">
        {data === null ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div> :
          data.orders.length === 0 ? <div className="py-12 text-center text-nardo-500 text-sm" data-testid="comedor-report-empty">Sin registros en el periodo.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-navy/5 text-navy text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-2">Fecha</th><th className="text-left px-2 py-2">Alumno</th><th className="text-left px-2 py-2">Grupo</th><th className="text-left px-2 py-2">Concepto</th><th className="text-right px-2 py-2">Costo</th>
              </tr></thead>
              <tbody className="divide-y divide-nardo-100">
                {data.orders.map((o) => (
                  <tr key={o.id}>
                    <td className="px-4 py-2 text-nardo-600">{o.date}</td>
                    <td className="px-2 py-2 font-medium text-navy">{o.student_name}</td>
                    <td className="px-2 py-2 text-nardo-600">{o.group_label || "—"}</td>
                    <td className="px-2 py-2 text-nardo-600">{conceptLabel(o.concept)} · {modalityLabel(o.modality)}</td>
                    <td className="px-2 py-2 text-right font-semibold text-emerald-700">{money(o.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>
    </div>
  );
}

function Precios({ prices, onSaved }) {
  const [form, setForm] = useState(() => prices || { desayuno: { dia: 0, mensual: 0 }, comida: { dia: 0, mensual: 0 }, completo: { dia: 0, mensual: 0 } });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (prices) setForm(prices); }, [prices]);

  const set = (c, m, v) => setForm((f) => ({ ...f, [c]: { ...f[c], [m]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/comedor/config", { prices: form });
      onSaved(data.prices);
      toast.success("Precios guardados");
    } catch { toast.error("No se pudieron guardar"); }
    finally { setSaving(false); }
  };
  const inp = "border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy w-full";

  return (
    <div className="bg-white border border-nardo-200 rounded-xl p-5 max-w-2xl" data-testid="comedor-precios">
      <h3 className="font-display font-bold text-navy mb-1 flex items-center gap-2"><DollarSign className="w-4 h-4 text-navy-light" /> Precios del comedor</h3>
      <p className="text-xs text-nardo-500 mb-4">Costos manuales que se sugieren al registrar (podrás editar el costo pagado en cada registro).</p>
      <div className="space-y-3">
        <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 text-[10px] uppercase tracking-wider text-nardo-500">
          <span>Concepto</span><span>Costo por día</span><span>Costo mensual</span>
        </div>
        {CONCEPTS.map((c) => (
          <div key={c.key} className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 items-center">
            <span className="text-sm font-semibold text-navy">{c.label}</span>
            <input type="number" min="0" step="0.01" value={form[c.key]?.dia ?? 0} onChange={(e) => set(c.key, "dia", e.target.value)} data-testid={`comedor-price-${c.key}-dia`} className={inp} />
            <input type="number" min="0" step="0.01" value={form[c.key]?.mensual ?? 0} onChange={(e) => set(c.key, "mensual", e.target.value)} data-testid={`comedor-price-${c.key}-mensual`} className={inp} />
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="comedor-price-save" className="mt-5 inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar precios</button>
    </div>
  );
}

const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const curMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthLabel = (m) => { const [y, mm] = (m || "").split("-"); return mm ? `${MONTHS_ES[+mm - 1]} ${y}` : m; };

function Adeudos({ students, prices }) {
  const [month, setMonth] = useState(curMonth());
  const [data, setData] = useState(null);
  const [student, setStudent] = useState(null);
  const [concept, setConcept] = useState("completo");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => {
    setData(null);
    api.get("/comedor/charges", { params: { month } }).then((r) => setData(r.data)).catch(() => setData({ charges: [], totals: { count: 0, pending: 0, paid: 0 } }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  useEffect(() => { if (prices && prices[concept]) setAmount(String(prices[concept].mensual || "")); }, [concept, prices]);

  const add = async () => {
    if (!student) { toast.error("Elige el alumno"); return; }
    setAdding(true);
    try {
      await api.post("/comedor/charges", { student_id: student.id, month, amount: Number(amount || 0), concept, note });
      toast.success("Adeudo registrado");
      setStudent(null); setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo registrar"); }
    finally { setAdding(false); }
  };
  const togglePay = async (id) => { try { await api.post(`/comedor/charges/${id}/pay`); load(); } catch { toast.error("No se pudo actualizar"); } };
  const remove = async (id) => { if (!window.confirm("¿Quitar este adeudo?")) return; try { await api.delete(`/comedor/charges/${id}`); load(); } catch { toast.error("No se pudo eliminar"); } };

  const t = data?.totals || { count: 0, pending: 0, paid: 0 };
  const inp = "border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy";

  return (
    <div className="space-y-4" data-testid="comedor-adeudos">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-navy font-medium">
          <CalendarDays className="w-4 h-4 text-navy-light" /> Mes:
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="comedor-charge-month" className={inp} />
        </label>
        <span className="text-xs text-nardo-500">Marca quién debe el comedor de <b className="text-navy">{monthLabel(month)}</b>. Aparece en Cobranza y en el Portal de Padres.</span>
      </div>

      <div className="bg-white border border-nardo-200 rounded-xl p-4">
        <div className="grid gap-3 md:grid-cols-[1.6fr_1fr_0.9fr_1.4fr_auto] items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Alumno</label>
            <StudentPicker students={students} value={student?.id} onPick={setStudent} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Concepto</label>
            <select value={concept} onChange={(e) => setConcept(e.target.value)} data-testid="comedor-charge-concept" className={`${inp} w-full`}>
              {CONCEPTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Monto adeudo</label>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="comedor-charge-amount" placeholder="0.00" className={`${inp} w-full`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Nota (opcional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="comedor-charge-note" placeholder="Ej. semanas 1 y 2" className={`${inp} w-full`} />
          </div>
          <button onClick={add} disabled={adding} data-testid="comedor-charge-add" className="inline-flex items-center justify-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 h-[38px]">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Marcar</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Adeudos</div><div className="text-2xl font-display font-extrabold text-navy">{t.count}</div></div>
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Por cobrar</div><div className="text-2xl font-display font-extrabold text-red-600">{money(t.pending)}</div></div>
        <div className="bg-white border border-nardo-200 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wider text-nardo-400">Pagado</div><div className="text-2xl font-display font-extrabold text-emerald-600">{money(t.paid)}</div></div>
      </div>

      <div className="bg-white border border-nardo-200 rounded-xl overflow-hidden">
        {data === null ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div> :
          data.charges.length === 0 ? <div className="py-12 text-center text-nardo-500 text-sm" data-testid="comedor-adeudos-empty">Sin adeudos de comedor en {monthLabel(month)}.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-navy/5 text-navy text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-2">Alumno</th><th className="text-left px-2 py-2">Grupo</th><th className="text-left px-2 py-2">Concepto</th><th className="text-left px-2 py-2">Nota</th><th className="text-right px-2 py-2">Monto</th><th className="text-center px-2 py-2">Estado</th><th className="px-2 py-2 w-24"></th>
              </tr></thead>
              <tbody className="divide-y divide-nardo-100">
                {data.charges.map((c) => (
                  <tr key={c.id} data-testid={`comedor-charge-row-${c.id}`}>
                    <td className="px-4 py-2 font-medium text-navy">{c.student_name}</td>
                    <td className="px-2 py-2 text-nardo-600">{c.group_label || "—"}</td>
                    <td className="px-2 py-2 text-nardo-600">{conceptLabel(c.concept) || "—"}</td>
                    <td className="px-2 py-2 text-nardo-500 text-xs">{c.note || "—"}</td>
                    <td className="px-2 py-2 text-right font-semibold text-navy">{money(c.amount)}</td>
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => togglePay(c.id)} data-testid={`comedor-charge-pay-${c.id}`} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${c.status === "pagado" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {c.status === "pagado" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} {c.status === "pagado" ? "Pagado" : "Pendiente"}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-right"><button onClick={() => remove(c.id)} data-testid={`comedor-charge-del-${c.id}`} className="inline-flex items-center justify-center border border-red-300 text-red-600 hover:bg-red-500 hover:text-white px-2 py-1 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>
    </div>
  );
}

const MENU_DAYS = [{ k: "lun", l: "Lunes" }, { k: "mar", l: "Martes" }, { k: "mie", l: "Miércoles" }, { k: "jue", l: "Jueves" }, { k: "vie", l: "Viernes" }];

function MenuSemanal() {
  const [menu, setMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/comedor/menu").then((r) => setMenu(r.data)).catch(() => setMenu({ title: "", note: "", days: {} })); }, []);

  const setDay = (d, field, v) => setMenu((m) => ({ ...m, days: { ...m.days, [d]: { ...(m.days[d] || {}), [field]: v } } }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/comedor/menu", menu); setMenu(data); toast.success("Menú publicado"); }
    catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };
  if (!menu) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>;
  const inp = "border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy w-full";

  return (
    <div className="bg-white border border-nardo-200 rounded-xl p-5 max-w-3xl" data-testid="comedor-menu">
      <h3 className="font-display font-bold text-navy mb-1 flex items-center gap-2"><BookOpenCheck className="w-4 h-4 text-navy-light" /> Menú semanal del comedor</h3>
      <p className="text-xs text-nardo-500 mb-4">Publica el menú para que los papás lo vean en su portal.</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <input value={menu.title} onChange={(e) => setMenu((m) => ({ ...m, title: e.target.value }))} data-testid="comedor-menu-title" placeholder="Título (ej. Semana del 28 al 1)" className={inp} />
        <input value={menu.note} onChange={(e) => setMenu((m) => ({ ...m, note: e.target.value }))} data-testid="comedor-menu-note" placeholder="Nota (ej. incluye fruta y agua)" className={inp} />
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-[100px_1fr_1fr] gap-3 text-[10px] uppercase tracking-wider text-nardo-500">
          <span>Día</span><span>Desayuno</span><span>Comida</span>
        </div>
        {MENU_DAYS.map((d) => (
          <div key={d.k} className="grid grid-cols-[100px_1fr_1fr] gap-3 items-center">
            <span className="text-sm font-semibold text-navy">{d.l}</span>
            <input value={(menu.days[d.k] || {}).desayuno || ""} onChange={(e) => setDay(d.k, "desayuno", e.target.value)} data-testid={`comedor-menu-${d.k}-desayuno`} placeholder="—" className={inp} />
            <input value={(menu.days[d.k] || {}).comida || ""} onChange={(e) => setDay(d.k, "comida", e.target.value)} data-testid={`comedor-menu-${d.k}-comida`} placeholder="—" className={inp} />
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="comedor-menu-save" className="mt-5 inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Publicar menú</button>
    </div>
  );
}
